import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Eye, MessageCircle, Share2, ThumbsUp, Clock, Tag } from "lucide-react";
import { fetchVideo, formatDate, formatDuration, formatNumber } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import AddToCollectionDialog from "@/components/AddToCollectionDialog";
import AdBanner from "@/components/AdBanner";
import { TipPrompt } from "@/components/TipPrompt";
import { ShareIcons } from "@/components/ShareIcons";
import { LevelNav, getGameLevel } from "@/components/LevelNav";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import usePageMeta from "@/hooks/use-page-meta";
import usePageJsonLd, { secondsToISODuration } from "@/hooks/use-page-jsonld";

export default function VideoDetail() {
  const { id } = useParams();
  const { data, isLoading, error } = useSWR(id ? `/videos/${id}` : null, () => fetchVideo(id));

  const cleanDesc = (data?.video?.description || "").split("\n").filter((l) => !/https?:|subscribe|playlist/i.test(l)).join(" ").replace(/\s+/g, " ").trim();
  usePageMeta({
    title: data?.video?.title || "Video",
    description: cleanDesc.length >= 40
      ? cleanDesc.slice(0, 155).trim() + (cleanDesc.length > 155 ? "…" : "")
      : data?.video
      ? `Watch "${data.video.title}" — a ${data.video.category || "gaming"} video from the tobiasdeamon archive, published ${new Date(data.video.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.`
      : undefined,
    image: data?.video?.thumbnail,
    type: "video.other",
  });

  // VideoObject JSON-LD — powers Google Video rich results.
  const v = data?.video;
  usePageJsonLd({
    key: "video",
    data: v
      ? {
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: v.title,
          description:
            (v.description || v.title || "").slice(0, 5000).replace(/\s+/g, " ").trim(),
          thumbnailUrl: [
            v.thumbnail,
            `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`,
            `https://i.ytimg.com/vi/${v.id}/maxresdefault.jpg`,
          ].filter(Boolean),
          uploadDate: v.published_at,
          duration: secondsToISODuration(v.duration_seconds),
          contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
          embedUrl: `https://www.youtube.com/embed/${v.id}`,
          publisher: {
            "@type": "Organization",
            name: "tobiasdeamon · Vault",
          },
          interactionStatistic: [
            v.view_count != null && {
              "@type": "InteractionCounter",
              interactionType: { "@type": "WatchAction" },
              userInteractionCount: v.view_count,
            },
            v.like_count != null && {
              "@type": "InteractionCounter",
              interactionType: { "@type": "LikeAction" },
              userInteractionCount: v.like_count,
            },
          ].filter(Boolean),
          keywords: (v.tags || []).slice(0, 20).join(", ") || undefined,
        }
      : null,
  });

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <Skeleton className="h-6 w-40 bg-white/5" />
        <Skeleton className="aspect-video w-full bg-white/5 rounded-2xl" />
        <Skeleton className="h-8 w-2/3 bg-white/5" />
      </div>
    );
  }

  if (error || !data?.video) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <div className="mono-label text-white/40">404</div>
        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">Video not found.</h1>
        <Link
          to="/archive"
          className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full border border-white/20 hover:border-[#d95d39] hover:text-[#d95d39] font-mono-tech text-xs uppercase tracking-[0.22em]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the archive
        </Link>
      </div>
    );
  }

  const related = data.related || [];
  const gameLevel = getGameLevel(v.title);

  const handleShare = async () => {
    const url = `https://www.youtube.com/watch?v=${v.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: v.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("YouTube link copied to clipboard");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="video-detail-modal">
      <Link
        to="/archive"
        className="inline-flex items-center gap-2 mono-label text-white/50 hover:text-[#d95d39] transition-colors mb-6"
        data-testid="back-to-archive"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Archive
      </Link>

      <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
        <div className="aspect-video">
          <iframe
            data-testid="video-embedded-player"
            src={`https://www.youtube.com/embed/${v.id}?rel=0`}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>

      <div className="mt-4">
        <ShareIcons label="Share this video" testid="video-share" showFollow={false} />
      </div>
      {process.env.REACT_APP_ADSENSE_VIDEO_SLOT && (
        <div className="mt-6">
          <AdBanner
            slot={process.env.REACT_APP_ADSENSE_VIDEO_SLOT}
            testid="ad-video-banner"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              {v.category && (
                <span className="mono-label px-3 py-1 rounded-full bg-[#d95d39]/10 border border-[#d95d39]/40 text-[#d95d39]">
                  {v.category}
                </span>
              )}
              <span className="mono-label text-white/40">{formatDate(v.published_at)}</span>
              <span className="mono-label text-white/40">{formatDuration(v.duration_seconds)}</span>
            </div>
            <h1 className="mt-4 font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#f4f1ea]">
              {v.title}
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-4 pb-6 border-b border-white/10">
            <Stat icon={Eye} value={formatNumber(v.view_count)} label="views" />
            <Stat icon={ThumbsUp} value={formatNumber(v.like_count)} label="likes" />
            <Stat icon={MessageCircle} value={formatNumber(v.comment_count)} label="comments" />
            <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">
              <AddToCollectionDialog videoId={v.id} videoTitle={v.title} />
              <Button
                onClick={handleShare}
                data-testid="video-share-btn"
                className="rounded-full bg-transparent border border-white/15 hover:border-[#d95d39] hover:text-[#d95d39] hover:bg-transparent font-mono-tech text-[11px] uppercase tracking-[0.22em]"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
              <a
                href={`https://www.youtube.com/watch?v=${v.id}`}
                target="_blank"
                rel="noreferrer"
                data-testid="video-open-youtube"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em]"
              >
                <ExternalLink className="w-4 h-4" /> <span className="hidden xs:inline">Open in </span>YouTube
              </a>
            </div>
          </div>

          {gameLevel && <LevelNav game={gameLevel.game} level={gameLevel.level} />}

          <TipPrompt />

          <div>
            <div className="mono-label mb-3">Description</div>
            <div
              data-testid="video-description-content"
              className="text-white/70 leading-relaxed whitespace-pre-wrap font-sans text-[15px] break-words [overflow-wrap:anywhere]"
            >
              {v.description || "No description provided."}
            </div>
          </div>

          {v.tags?.length > 0 && (
            <div>
              <div className="mono-label mb-3 inline-flex items-center gap-2">
                <Tag className="w-3.5 h-3.5" /> Tags
              </div>
              <div className="flex flex-wrap gap-2">
                {v.tags.slice(0, 30).map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 rounded-full text-xs bg-white/[0.04] border border-white/10 text-white/60 break-all max-w-full"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="mono-label">Related in {v.category}</div>
          <div className="space-y-4">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/video/${r.id}`}
                className="group flex gap-3 rounded-xl overflow-hidden border border-white/10 bg-[#12141d] hover:border-[#d95d39]/50 transition-colors"
              >
                <div className="relative w-40 flex-shrink-0 aspect-video bg-black">
                  <img
                    src={r.thumbnail || `https://i.ytimg.com/vi/${r.id}/mqdefault.jpg`}
                    alt={r.title}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute bottom-1 right-1 font-mono-tech text-[10px] px-1.5 py-0.5 rounded bg-black/80">
                    {formatDuration(r.duration_seconds)}
                  </span>
                </div>
                <div className="p-3 pr-4 flex-1">
                  <div className="text-sm text-[#f4f1ea] line-clamp-2 group-hover:text-[#d95d39]">{r.title}</div>
                  <div className="mono-label text-white/40 mt-2 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(r.published_at)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="inline-flex items-center gap-2 text-white/70">
      <Icon className="w-4 h-4 text-[#d95d39]" />
      <span className="font-serif-editorial text-xl text-[#f4f1ea]">{value}</span>
      <span className="mono-label text-white/40">{label}</span>
    </div>
  );
}
